import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material"
import { IAiPrompts, IAiPromptsInput } from "@pages/main/scripts/prompts"
import {
  formatModelSettings,
  promptLayerLabel,
  promptRoleLabel
} from "@pages/main/scripts/prompts/prompt.models"
import {
  hasDiff,
  summarizeDiff
} from "@pages/main/scripts/prompts/sub_components/diffSummary"
import PromptDiffPanel, {
  DiffStat
} from "@pages/main/scripts/prompts/sub_components/PromptDiffPanel"
import { useMemo, useState } from "react"
import {
  CURRENT_VERSION_INDEX,
  versionIndexInRange,
  versionLabel
} from "./promptVersions"

interface PromptVersionDiffProps {
  open: boolean
  onClose: () => void
  prompt: IAiPrompts
  title?: string
  maxWidth?: "md" | "lg" | "xl"
  defaultRightIndex?: number
  comparePrompt?: IAiPromptsInput | null
  leftStaticLabel?: string
  rightStaticLabel?: string
  hideVersionSelectors?: boolean
  onLoadVersion?: (version: IAiPromptsInput) => void
  onRollbackVersion?: (version: IAiPromptsInput, label: string) => void
  rollbacking?: boolean
}

interface MetadataChange {
  field: string
  oldValue: string
  newValue: string
}

function getMetadataChanges(
  v1:
    | IAiPrompts
    | IAiPromptsInput
    | NonNullable<IAiPrompts["versions"]>[number]
    | undefined,
  v2:
    | IAiPrompts
    | IAiPromptsInput
    | NonNullable<IAiPrompts["versions"]>[number]
    | undefined
): MetadataChange[] {
  const changes: MetadataChange[] = []

  if ((v1?.role ?? null) !== (v2?.role ?? null)) {
    changes.push({
      field: "AI role",
      oldValue: promptRoleLabel(v1?.role),
      newValue: promptRoleLabel(v2?.role)
    })
  }

  if ((v1?.engineVersion ?? null) !== (v2?.engineVersion ?? null)) {
    changes.push({
      field: "Engine layer",
      oldValue: promptLayerLabel(v1?.engineVersion),
      newValue: promptLayerLabel(v2?.engineVersion)
    })
  }

  if (v1?.usage !== v2?.usage) {
    changes.push({
      field: "Usage",
      oldValue: v1?.usage ?? "",
      newValue: v2?.usage ?? ""
    })
  }

  if (v1?.aiModel !== v2?.aiModel) {
    changes.push({
      field: "AI Model",
      oldValue: v1?.aiModel ?? "",
      newValue: v2?.aiModel ?? ""
    })
  }

  const oldSettings = formatModelSettings(v1?.modelSettings)
  const newSettings = formatModelSettings(v2?.modelSettings)
  if (oldSettings !== newSettings) {
    changes.push({
      field: "Model settings",
      oldValue: oldSettings,
      newValue: newSettings
    })
  }

  return changes
}

function toPromptInput(
  version:
    | IAiPrompts
    | IAiPromptsInput
    | NonNullable<IAiPrompts["versions"]>[number]
    | undefined
): IAiPromptsInput | null {
  if (!version) return null

  return {
    name: version.name,
    description: version.description,
    role: version.role ?? null,
    usage: version.usage,
    aiModel: version.aiModel,
    responseSchema: version.responseSchema ?? null,
    modelSettings: version.modelSettings ?? null
  }
}

export default function PromptVersionDiff({
  open,
  onClose,
  prompt,
  title = "Prompt Version Diff",
  maxWidth = "xl",
  defaultRightIndex = 0,
  comparePrompt = null,
  leftStaticLabel,
  rightStaticLabel,
  hideVersionSelectors = false,
  onLoadVersion,
  onRollbackVersion,
  rollbacking = false
}: PromptVersionDiffProps) {
  const [selectedLeft, setLeftIndex] = useState<number>(
    prompt.versions?.length ? defaultRightIndex : -1
  )
  const [selectedRight, setRightIndex] = useState<number>(-1)

  const versionCount = prompt.versions?.length ?? 0
  // `versions[0]` is the state BEFORE the last save, so a fixed comparison must start from the
  // live row — otherwise "CURRENT ↔ DRAFT" shows the last save and the draft mixed together.
  const leftIndex = comparePrompt
    ? CURRENT_VERSION_INDEX
    : versionIndexInRange(selectedLeft, versionCount)
  const rightIndex = versionIndexInRange(selectedRight, versionCount)

  const leftVersion = useMemo(() => {
    if (leftIndex === -1) return prompt
    return prompt.versions?.[leftIndex]
  }, [leftIndex, prompt])

  const rightVersion = useMemo(() => {
    if (comparePrompt) return comparePrompt
    if (rightIndex === -1) return prompt
    return prompt.versions?.[rightIndex]
  }, [comparePrompt, rightIndex, prompt])

  const leftPromptText = useMemo(() => {
    if (leftIndex === -1) return prompt.description
    return prompt.versions?.[leftIndex]?.description ?? ""
  }, [leftIndex, prompt])

  const rightPromptText = useMemo(() => {
    if (comparePrompt) return comparePrompt.description
    if (rightIndex === -1) return prompt.description
    return prompt.versions?.[rightIndex]?.description ?? ""
  }, [comparePrompt, rightIndex, prompt])

  const leftSchemaText = useMemo(() => {
    const schema =
      leftIndex === -1
        ? prompt.responseSchema
        : prompt.versions?.[leftIndex]?.responseSchema
    return schema ? JSON.stringify(schema, null, 2) : ""
  }, [leftIndex, prompt])

  const rightSchemaText = useMemo(() => {
    if (comparePrompt) {
      return comparePrompt.responseSchema
        ? JSON.stringify(comparePrompt.responseSchema, null, 2)
        : ""
    }
    const schema =
      rightIndex === -1
        ? prompt.responseSchema
        : prompt.versions?.[rightIndex]?.responseSchema
    return schema ? JSON.stringify(schema, null, 2) : ""
  }, [comparePrompt, rightIndex, prompt])

  const metadataChanges = useMemo(() => {
    return getMetadataChanges(leftVersion, rightVersion)
  }, [leftVersion, rightVersion])

  const labelFor = (idx: number) =>
    versionLabel(idx, prompt.versions?.[idx]?.updatedAt)

  const leftLabel = leftStaticLabel ?? labelFor(leftIndex)
  const rightLabel =
    rightStaticLabel ?? (comparePrompt ? "DRAFT" : labelFor(rightIndex))

  const leftInput = useMemo(() => toPromptInput(leftVersion), [leftVersion])
  const rightInput = useMemo(() => toPromptInput(rightVersion), [rightVersion])

  const VersionLabel = ({
    index,
    otherIndex
  }: {
    index: number
    otherIndex: number
  }) => {
    const label =
      index === leftIndex
        ? (leftStaticLabel ?? labelFor(index))
        : (rightStaticLabel ??
          (comparePrompt && index === rightIndex ? "DRAFT" : labelFor(index)))

    const v1 = index === -1 ? prompt : prompt.versions?.[index]
    const v2 = otherIndex === -1 ? prompt : prompt.versions?.[otherIndex]

    const mCount = getMetadataChanges(v1, v2).length

    const p1 =
      index === -1 ? prompt.description : prompt.versions?.[index]?.description
    const p2 =
      otherIndex === -1
        ? prompt.description
        : prompt.versions?.[otherIndex]?.description
    const pStats = summarizeDiff(p1 ?? "", p2 ?? "")

    const getS = (
      v:
        | IAiPrompts
        | IAiPromptsInput
        | NonNullable<IAiPrompts["versions"]>[number]
        | undefined
    ) => (v?.responseSchema ? JSON.stringify(v.responseSchema, null, 2) : "")
    const sStats = summarizeDiff(getS(v1), getS(v2))
    const sCount = sStats.additions + sStats.deletions

    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        spacing={2}
      >
        <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
          {label}
        </Typography>
        {(mCount > 0 || hasDiff(pStats) || sCount > 0) && (
          <Stack direction="row" spacing={1} alignItems="center">
            {hasDiff(pStats) && (
              <DiffStat summary={pStats} showBlocks={false} />
            )}
            {sCount > 0 && (
              <Typography
                variant="caption"
                sx={{ color: "info.main", fontWeight: "bold" }}
                title="Response Schema Diffs"
              >
                S:{sCount}
              </Typography>
            )}
            {mCount > 0 && (
              <Typography
                variant="caption"
                sx={{ color: "error.main", fontWeight: "bold" }}
                title="Metadata Diffs"
              >
                M:{mCount}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    )
  }

  const VersionSelect = ({
    value,
    onChange,
    side
  }: {
    value: number
    onChange: (n: number) => void
    side: "Left" | "Right"
  }) => {
    const otherIndex = side === "Left" ? rightIndex : leftIndex

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2" sx={{ minWidth: 56 }}>
          {side}
        </Typography>
        <Select
          size="small"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          displayEmpty
          sx={{ minWidth: 200 }}
          renderValue={(selected) => (
            <VersionLabel index={selected} otherIndex={otherIndex} />
          )}
        >
          <MenuItem value={-1}>
            <VersionLabel index={-1} otherIndex={otherIndex} />
          </MenuItem>
          {prompt.versions?.map((_, i) => (
            <MenuItem key={i} value={i}>
              <VersionLabel index={i} otherIndex={otherIndex} />
            </MenuItem>
          ))}
        </Select>
      </Stack>
    )
  }

  const MetadataChip = ({ count }: { count: number }) => {
    if (count === 0) {
      return (
        <Chip label="No changes" size="small" color="default" sx={{ ml: 1 }} />
      )
    }

    return (
      <Chip
        label={`${count} field${count > 1 ? "s" : ""} changed`}
        size="small"
        color="warning"
        sx={{ ml: 1 }}
      />
    )
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={maxWidth}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {hideVersionSelectors ? (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ mb: 2 }}
          >
            <Chip label={leftLabel} size="small" />
            <Chip label={rightLabel} size="small" color="primary" />
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              {prompt.name}
            </Typography>
          </Stack>
        ) : (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <VersionSelect
              value={leftIndex}
              onChange={setLeftIndex}
              side="Left"
            />
            <VersionSelect
              value={rightIndex}
              onChange={setRightIndex}
              side="Right"
            />
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              {prompt.name}
            </Typography>
          </Stack>
        )}

        {(onLoadVersion || onRollbackVersion) && (
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            {leftIndex !== -1 && leftInput && (
              <>
                <Button
                  variant="outlined"
                  onClick={() => onLoadVersion?.(leftInput)}
                >
                  Load {labelFor(leftIndex)}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  disabled={rollbacking}
                  onClick={() =>
                    onRollbackVersion?.(leftInput, labelFor(leftIndex))
                  }
                >
                  Rollback to {labelFor(leftIndex)}
                </Button>
              </>
            )}
            {rightIndex !== -1 && rightInput && (
              <>
                <Button
                  variant="outlined"
                  onClick={() => onLoadVersion?.(rightInput)}
                >
                  Load {labelFor(rightIndex)}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  disabled={rollbacking}
                  onClick={() =>
                    onRollbackVersion?.(rightInput, labelFor(rightIndex))
                  }
                >
                  Rollback to {labelFor(rightIndex)}
                </Button>
              </>
            )}
          </Stack>
        )}

        {metadataChanges.length > 0 && (
          <Paper variant="outlined" sx={{ mb: 1.5, p: 1.5 }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Metadata
              </Typography>
              <MetadataChip count={metadataChanges.length} />
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Field</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Old Value</strong>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 50 }}>
                      {" "}
                    </TableCell>
                    <TableCell>
                      <strong>New Value</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metadataChanges.map((change, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {change.field}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={change.oldValue}
                          size="small"
                          sx={{
                            backgroundColor: "#f8d7da",
                            color: "#721c24"
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <ArrowForwardIcon
                          fontSize="small"
                          sx={{ opacity: 0.5 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={change.newValue}
                          size="small"
                          sx={{
                            backgroundColor: "#d4edda",
                            color: "#155724"
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <PromptDiffPanel
          title="Prompt"
          oldValue={leftPromptText}
          newValue={rightPromptText}
        />

        {(leftSchemaText || rightSchemaText) && (
          <PromptDiffPanel
            key={`${leftSchemaText === rightSchemaText}`}
            title="Response Schema"
            oldValue={leftSchemaText}
            newValue={rightSchemaText}
            defaultExpanded={leftSchemaText !== rightSchemaText}
          />
        )}

        {!leftSchemaText && !rightSchemaText && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, textAlign: "center" }}
          >
            No response schema defined for these versions
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
